import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useGetActionsQuery } from "app/services/missionStorageApiEnhanced";
import { Action } from "app/services/types.generated";
import {
    resolveActiveMissionId,
    resolveMapWaypointMissionId,
} from "lib/mission/activeMission";
import {
    navigateToActionsToMapWaypoints,
    missionActionsToMapMarkers,
    type MapWaypoint,
    type MissionMapActionMarker,
} from "lib/mission/missionMapWaypoints";
import { useMissionExecution } from "providers/navigation/MissionExecutionProvider";
import type { RootState } from "store";

export type MissionWaypointSource = "executing" | "editing";

export interface MissionMapWaypointsInfo {
    waypoints: MapWaypoint[];
    /** Non-navigation items (photo/wait/stair/dock) anchored to their waypoint. */
    actionMarkers: MissionMapActionMarker[];
    /** Mission the pins belong to. */
    missionId: string | null;
    /** Where the pins come from: a live run, or the mission open in create/edit. */
    source: MissionWaypointSource | null;
    /** NavigateTo actions backing the pins (sorted by sequence order). */
    actions: Action[];
}

/**
 * NavigateTo waypoints for the map overlay.
 * Shows the executing mission when one is active; otherwise the mission open
 * in create/edit (`missionCreation.currentMissionId`).
 */
export const useMissionMapWaypoints = (): MissionMapWaypointsInfo => {
    const { executorState, isExecuting, currentMissionId } = useMissionExecution();
    const editingMissionId = useSelector(
        (state: RootState) => state.missionCreation.currentMissionId,
    );

    const activeMissionId = resolveActiveMissionId({
        executorState,
        isExecuting,
        currentMissionId,
    });
    const source: MissionWaypointSource | null = activeMissionId
        ? "executing"
        : editingMissionId
          ? "editing"
          : null;

    const missionIdForMap = resolveMapWaypointMissionId({
        activeMissionId,
        editingMissionId,
    });

    const { data: actionsData } = useGetActionsQuery(
        { missionId: missionIdForMap || "" },
        { skip: !missionIdForMap },
    );

    return useMemo(() => {
        if (!missionIdForMap) {
            return {
                waypoints: [],
                actionMarkers: [],
                missionId: null,
                source: null,
                actions: [],
            };
        }
        const actions = (actionsData?.actions || []) as Action[];
        return {
            waypoints: navigateToActionsToMapWaypoints(actions),
            actionMarkers: missionActionsToMapMarkers(actions),
            missionId: missionIdForMap,
            source,
            actions: actions
                .filter((action) => action.actionType === "NavigateTo")
                .sort((a, b) => a.sequenceOrder - b.sequenceOrder),
        };
    }, [missionIdForMap, source, actionsData?.actions]);
};
